/**
 * Game Service
 * ดึงข้อมูลเกมจาก Parse Backend
 */

import { queryClass, createObject, updateObject } from '@/services/adapters/parse/parse-client'
import { Game, GameCategory, GameProvider, GameQueryParams, GameInput, GameStatus } from './game.types'

// Helper: แปลง Parse object เป็น Game
function parseGameToUI(raw: any): Game {
  return {
    id: raw.objectId,
    objectId: raw.objectId,
    game_id: raw.game_id || '',
    gameName: raw.gameName || '',
    gameNameTh: raw.gameNameTh || raw.gameName,
    game_Category: raw.game_Category || '',
    provider: raw.provider || '',
    image: raw.image,
    image_vertical: raw.image?.vertical || raw.image_vertical,
    status: raw.status || 'INACTIVE',
    isHot: raw.isHot || false,
    isNew: raw.isNew || false,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }
}

// Helper: แปลง Parse object เป็น GameProvider
// รองรับ fields จาก game-sync-service (provider, providerName, logoURL, etc.)
function parseProviderToUI(raw: any): GameProvider {
  // Get name with fallback
  const name = raw.providerName || raw.name || raw.canonical || raw.provider || ''
  // Get code with safe fallback
  const code = raw.provider || raw.code || (typeof name === 'string' ? name.toUpperCase().replace(/\s+/g, '') : '')
  // Get id with fallback to code
  const id = raw.objectId || code || `provider_${Math.random().toString(36).substr(2, 9)}`
  
  return {
    id,
    objectId: raw.objectId || id,
    name,
    code,
    // logo: รองรับทั้ง logoURL และ logo object
    logo: raw.logoURL || raw.logoTransparentURL || raw.logo?.url || raw.logo || '',
    // providerType: เพิ่ม field ใหม่
    providerType: raw.providerType || (Array.isArray(raw.gameType) ? raw.gameType[0] : raw.gameType) || '',
    gamesCount: raw.gamesCount || 0,
    activeGames: raw.activeGames || 0,
    status: raw.status || 'ACTIVE',
    isActive: raw.isActive !== false, // default true
    apiStatus: raw.apiStatus || (raw.status === 'ACTIVE' ? 'connected' : 'disconnected'),
    lastSyncAt: raw.lastSyncAt ? (raw.lastSyncAt.iso || raw.lastSyncAt) : raw.updatedAt,
    createdAt: raw.createdAt,
  }
}

export const GameService = {
  /**
   * ดึงรายการเกมทั้งหมด
   */
  async findAllGames(params: GameQueryParams = {}): Promise<{ items: Game[]; total: number }> {
    console.log('🎮 [GameService] findAllGames:', params)
    
    const where: Record<string, any> = {
      deletedAt: { $exists: false },
    }
    
    if (params.query) {
      where.gameName = { $regex: params.query, $options: 'i' }
    }
    
    if (params.category) {
      // รองรับทั้ง category (จาก sync) และ game_Category (legacy)
      where.$or = [
        { category: params.category },
        { game_Category: params.category },
      ]
    }
    
    if (params.provider) {
      // Provider code from GameList
      where.provider = params.provider
    }
    
    if (params.status) {
      where.status = params.status
    }
    
    let order = '-createdAt'
    if (params.sort) {
      order = params.sort.direction === 'asc' ? params.sort.field : `-${params.sort.field}`
    }
    
    const result = await queryClass('GameList', {
      where,
      limit: params.limit || 50,
      skip: params.page ? (params.page - 1) * (params.limit || 50) : 0,
      order,
      count: 1,
    })
    
    console.log('🎮 [GameService] Result:', { total: result.count, items: result.results?.length })
    
    return {
      items: (result.results || []).map(parseGameToUI),
      total: result.count || 0,
    }
  },
  
  /**
   * ดึงเกมเดียว
   */
  async findOneGame(id: string): Promise<Game | null> {
    const result = await queryClass('GameList', {
      where: { objectId: id },
      limit: 1,
    })
    
    if (result.results?.length > 0) {
      return parseGameToUI(result.results[0])
    }
    return null
  },
  
  /**
   * สร้างเกมใหม่
   */
  async createGame(data: GameInput): Promise<Game> {
    const result = await createObject('GameList', {
      ...data,
      status: data.status || 'INACTIVE',
    })
    return parseGameToUI(result)
  },
  
  /**
   * อัพเดทเกม
   */
  async updateGame(id: string, data: Partial<GameInput>): Promise<Game> {
    const result = await updateObject('GameList', id, data)
    return parseGameToUI(result)
  },
  
  /**
   * เปลี่ยนสถานะเกม
   */
  async toggleGameStatus(id: string, status: GameStatus): Promise<Game> {
    return this.updateGame(id, { status })
  },
  
  /**
   * Soft delete เกม
   */
  async deleteGame(id: string): Promise<void> {
    await updateObject('GameList', id, { deletedAt: new Date().toISOString() })
  },
  
  /**
   * เปลี่ยนสถานะหลายเกมพร้อมกัน
   */
  async bulkToggleGames(ids: string[], status: GameStatus): Promise<void> {
    await Promise.all(ids.map(id => this.toggleGameStatus(id, status)))
  },
  
  // ============================================
  // Categories
  // ============================================
  
  /**
   * ดึงประเภทเกมทั้งหมด
   * ใช้ aggregate จาก GameList.category เพราะ Category class อาจว่างเปล่า
   */
  async findAllCategories(params: { query?: string } = {}): Promise<{ items: GameCategory[]; total: number }> {
    console.log('📂 [GameService] findAllCategories:', params)
    
    // ใช้ getCategoriesFromGames แทนการดึงจาก Category class
    const result = await this.getCategoriesFromGames()
    
    // Filter by query if provided
    if (params.query) {
      const query = params.query.toLowerCase()
      const filtered = result.items.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.nameTh?.toLowerCase().includes(query)
      )
      return {
        items: filtered,
        total: filtered.length,
      }
    }
    
    return result
  },

  /**
   * ดึงประเภทเกมจาก GameList.category (aggregate)
   * ใช้สำหรับ Categories Page ที่ต้องการนับจำนวนเกมในแต่ละ category
   */
  async getCategoriesFromGames(): Promise<{ items: GameCategory[]; total: number }> {
    console.log('📂 [GameService] getCategoriesFromGames - Starting')
    
    // Fixed categories with Thai names
    const FIXED_CATEGORIES: Record<string, { name: string; nameTh: string; icon: string; order: number }> = {
      'SLOT': { name: 'Slots', nameTh: 'สล็อต', icon: '🎰', order: 1 },
      'CASINO': { name: 'Live Casino', nameTh: 'คาสิโนสด', icon: '🃏', order: 2 },
      'SPORT': { name: 'Sports', nameTh: 'กีฬา', icon: '⚽', order: 3 },
      'FISH': { name: 'Fishing', nameTh: 'ยิงปลา', icon: '🐟', order: 4 },
      'KENO': { name: 'Keno', nameTh: 'คีโน', icon: '🎱', order: 5 },
      'CARD': { name: 'Card Games', nameTh: 'ไพ่', icon: '🂡', order: 6 },
      'POKER': { name: 'Poker', nameTh: 'โป๊กเกอร์', icon: '♠️', order: 7 },
      'TRADE': { name: 'Trade', nameTh: 'เทรด', icon: '📈', order: 8 },
      'ESPORT': { name: 'E-Sports', nameTh: 'อีสปอร์ต', icon: '🎮', order: 9 },
      'OTHER': { name: 'Other', nameTh: 'อื่นๆ', icon: '🕹️', order: 10 },
    }
    
    // Query all games and group by category
    const result = await queryClass('GameList', {
      where: {},
      limit: 100000,
      keys: ['category', 'isActive'],
    })
    
    console.log('📂 [GameService] GameList query result:', { 
      resultsLength: result.results?.length,
      sampleCategories: result.results?.slice(0, 5).map((g: any) => g.category),
    })
    
    // Count games per category
    const categoryCounts: Record<string, { total: number; active: number }> = {}
    
    for (const game of (result.results || [])) {
      const cat = game.category || 'OTHER'
      if (!categoryCounts[cat]) {
        categoryCounts[cat] = { total: 0, active: 0 }
      }
      categoryCounts[cat].total++
      if (game.isActive !== false) {
        categoryCounts[cat].active++
      }
    }
    
    console.log('📂 [GameService] categoryCounts:', categoryCounts)
    
    // Build category items
    const items: GameCategory[] = Object.entries(FIXED_CATEGORIES)
      .filter(([key]) => categoryCounts[key]?.total > 0 || key === 'SLOT' || key === 'CASINO')
      .map(([key, config]) => ({
        id: key,
        objectId: key,
        name: config.name,
        nameTh: config.nameTh,
        icon: config.icon,
        gamesCount: categoryCounts[key]?.total || 0,
        activeGames: categoryCounts[key]?.active || 0,
        status: 'ACTIVE' as const,
        order: config.order,
      }))
      .sort((a, b) => (a.order || 0) - (b.order || 0))
    
    console.log('📂 [GameService] Final categories:', items.length, items)
    
    // Add any extra categories from games that are not in FIXED_CATEGORIES
    for (const [cat, counts] of Object.entries(categoryCounts)) {
      if (!FIXED_CATEGORIES[cat]) {
        items.push({
          id: cat,
          objectId: cat,
          name: cat,
          nameTh: cat,
          icon: '🎮',
          gamesCount: counts.total,
          activeGames: counts.active,
          status: 'ACTIVE',
          order: 99,
        })
      }
    }
    
    return {
      items,
      total: items.length,
    }
  },
  
  /**
   * เปลี่ยนสถานะประเภท
   */
  async toggleCategoryStatus(id: string, status: GameStatus): Promise<void> {
    await updateObject('Category', id, { status })
  },
  
  // ============================================
  // Providers
  // ============================================
  
  /**
   * ดึงผู้ให้บริการทั้งหมด พร้อมนับจำนวนเกม
   * Fields จาก game-sync-service: provider (code), providerName (name), providerType, logoURL, etc.
   */
  async findAllProviders(params: { query?: string } = {}): Promise<{ items: GameProvider[]; total: number }> {
    console.log('🏢 [GameService] findAllProviders:', params)
    
    const where: Record<string, any> = {}
    
    if (params.query) {
      // Search by provider (code) or providerName (name)
      where.$or = [
        { providerName: { $regex: params.query, $options: 'i' } },
        { provider: { $regex: params.query, $options: 'i' } },
      ]
    }
    
    // Fetch providers
    const result = await queryClass('Provider', {
      where,
      order: 'providerName,-createdAt',
      count: 1,
      limit: 1000,
    })
    
    console.log('🏢 [GameService] Provider query result:', { 
      count: result.count, 
      resultsLength: result.results?.length,
      firstItem: result.results?.[0],
    })
    
    // Count games per provider from GameList
    const gamesResult = await queryClass('GameList', {
      where: {},
      limit: 100000,
      keys: ['provider', 'isActive'],
    })
    
    console.log('🏢 [GameService] GameList for provider count:', { 
      gamesCount: gamesResult.results?.length,
    })
    
    // Build game counts map
    const gamesCounts: Record<string, { total: number; active: number }> = {}
    for (const game of (gamesResult.results || [])) {
      const prov = game.provider || ''
      if (!gamesCounts[prov]) {
        gamesCounts[prov] = { total: 0, active: 0 }
      }
      gamesCounts[prov].total++
      if (game.isActive !== false) {
        gamesCounts[prov].active++
      }
    }
    
    // Map providers with game counts
    const items = (result.results || []).map((raw: any) => {
      const provider = parseProviderToUI(raw)
      const code = raw.provider || provider.code
      provider.gamesCount = gamesCounts[code]?.total || 0
      provider.activeGames = gamesCounts[code]?.active || 0
      return provider
    })
    
    console.log('🏢 [GameService] Final providers:', { 
      total: result.count || 0, 
      items: items.length,
      firstProvider: items[0],
    })
    
    return {
      items,
      total: result.count || 0,
    }
  },
  
  /**
   * เปลี่ยนสถานะผู้ให้บริการ
   * ใช้ทั้ง status และ isActive เพื่อ compatibility
   */
  async toggleProviderStatus(id: string, status: GameStatus): Promise<void> {
    await updateObject('Provider', id, { 
      status,
      isActive: status === 'ACTIVE',
    })
  },
}











